import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import Debug from 'debug';
import { cloneDeep } from 'lodash';
import { SpreadSheetPreviewHelper } from '../../helpers/spreadsheet-preview.helper';
import { IHighlightedRows } from '../../interfaces/IHighlightedRows.interface';
import { ISheet } from '../../interfaces/ISheet';
import { ISheetRow } from '../../interfaces/ISheetRow';
import { ISpreadSheet } from '../../interfaces/ISpreadSheet';
import { ISpreadSheetClick } from '../../interfaces/ISpreadSheetClick.interface';

const debug = Debug('aumico:aumico-lib-annualreport-fe:SpreadSheetPreviewComponent');

const column0Placeholder = 'column0';
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'aumico-annualReport-spreadsheet-preview-component',
    templateUrl: 'spreadsheet-preview.component.html',
    styleUrls: ['spreadsheet-preview.component.scss'],
})
export class SpreadSheetPreviewComponent implements OnInit, OnChanges {
    // must be of type <string, object[]> because we don't have a definite interface for what comes from the imported file
    sheetPreviewMap = new Map<string, object[]>();
    sheetColumnsMap = new Map<string, number>();
    sheetColumnsClassMap = new Map<string, string>();
    currentSheet: string;
    private currentColumnNames: string[] = [];
    spreadSheetData: ISpreadSheet;

    @Input() public set data(value: ISpreadSheet) {
        this.spreadSheetData = cloneDeep(value);
        this.setColumnLengths();
        this.mapSpreadSheetDataToTableData();
    }

    @Input() balanceHighlightedRows: IHighlightedRows = {} as IHighlightedRows;
    @Input() incomeHighlightedRows: IHighlightedRows = {} as IHighlightedRows;
    @Output() spreadSheetClick: EventEmitter<ISpreadSheetClick> = new EventEmitter();

    dataSource: MatTableDataSource<object>;

    constructor() { }

    ngOnChanges(changes: SimpleChanges) {
        if (changes != null && changes['balanceHighlightedRows'] != null) {
            this.balanceHighlightedRows = changes['balanceHighlightedRows'].currentValue;
        }
        if (changes != null && changes['incomeHighlightedRows'] != null) {
            this.incomeHighlightedRows = changes['incomeHighlightedRows'].currentValue;
        }
    }

    ngOnInit() {
        if (this.spreadSheetData != null) {
            this.checkWhichColsAreNumbers();
        } else {
            this.dataSource = null;
        }
    }

    private mapSpreadSheetDataToTableData(): void {
        // clear sheets in map
        this.sheetPreviewMap.clear();
        this.spreadSheetData.sheets.forEach((sheet: ISheet) => {
            // must be of type object[] because we don't have a definite interface for what comes from the imported file
            const tableData: object[] = [];
            // this.sheetColumnsMap.get(sheet.name) get the number of columns (length) for this sheet
            const colNames: string[] = this.getColumnNames(this.sheetColumnsMap.get(sheet.name));

            // Adding columnNames (letters) in first row
            const columnLettersRow: ISheetRow = { cells: colNames.filter((name) => name !== column0Placeholder) };
            sheet.rows.unshift(columnLettersRow);

            sheet.rows.forEach((row: ISheetRow, rowCounter: number) => {
                // must be of type object because we don't have a definite interface for what comes from the imported file
                const tableRow: object = {};

                colNames.forEach((colName: string, index: number) => {
                    // column0 is always for the row numbers
                    if (colName === column0Placeholder) {
                        // if column 0 then we don't want to add 0 in the row numbers
                        tableRow[colName] = rowCounter !== 0 ? rowCounter : '';
                    } else {
                        // this -1 is because the index is automatically incremented when it enters the if statement
                        // and we don't want this so we move a step back
                        tableRow[colName] = row ? row.cells[index - 1] : '';
                    }
                });
                tableData.push(tableRow);
            });
            this.sheetPreviewMap.set(sheet.name, tableData);
        });
        const sheetNames = Array.from(this.sheetPreviewMap.keys());
        // initially, the current sheet is the first one
        this.currentSheet = sheetNames[0];
        // initially, the dataSource is set to have the data of the first sheet (index 0)
        this.dataSource = new MatTableDataSource(this.sheetPreviewMap.get(sheetNames[0]));
        debug(this.sheetPreviewMap);
    }

    // calculating number of columns for each sheet by finding the maximum number of cells/row
    private setColumnLengths(): void {
        this.spreadSheetData.sheets.forEach((sheet: ISheet) => {
            const rows: ISheetRow[] = sheet.rows.filter((row) => row != null);
            const rowsLengths: number[] = [];
            rows.forEach((row: ISheetRow) => {
                if (row != null) {
                    rowsLengths.push(row.cells.length);
                }
            });
            if (rowsLengths.length != null) {
                const columnLength = Math.max(...rowsLengths);
                debug('No of columns', columnLength);
                this.sheetColumnsMap.set(sheet.name, columnLength);
            } else {
                this.sheetColumnsMap.set(sheet.name, -1);
            }
        });
    }

    // Create custom column names according to length of rows
    getColumnNames(columnLength: number): string[] {
        const columnNames: string[] = [];
        // index 0 gives and empty string when converted to alphabet so we add a placeholder for it
        // the +1 is to add an extra column for the row numbers
        for (let index = 0; index < columnLength + 1; index++) {
            if (index === 0) {
                columnNames.push(column0Placeholder);
            } else {
                const columnLetter = SpreadSheetPreviewHelper.getColumnNameFromColumnNumber(index);
                columnNames.push(columnLetter);
            }
        }
        return columnNames;
    }

    private checkWhichColsAreNumbers(): void {
        if (this.spreadSheetData != null) {
            const sheetNames: string[] = Array.from(this.sheetPreviewMap.keys());

            const data = this.sheetPreviewMap.get(sheetNames[0]);
            const colNames: string[] = this.getColumnNames(this.sheetColumnsMap.get(sheetNames[0]));
            this.sheetColumnsClassMap = new Map<string, string>();
            colNames.forEach((colName) => {
                const mapped = data.map((value) => value[colName]).filter((value) => !Number.isNaN(Number(value)));

                debug('mappedC:', mapped, colName);
                if (mapped.length > data.length / 2) {
                    this.sheetColumnsClassMap.set(colName, 'number');
                } else {
                    this.sheetColumnsClassMap.set(colName, 'string');
                }
            });
        }
    }

    onButtonToggleClick(sheetName: string) {
        this.currentSheet = sheetName;
        this.dataSource = new MatTableDataSource(this.sheetPreviewMap.get(sheetName));
        const clickEventData: ISpreadSheetClick = {
            sheetName: this.currentSheet,
        };
        this.spreadSheetClick.emit(clickEventData);
    }

    onCellClick(row: number, col: number) {
        // row is 1-based and column is 0-based so we add +1 to column
        const clickEventData: ISpreadSheetClick = {
            sheetName: this.currentSheet,
            rowNo: row,
            colNo: col + 1,
        };
        debug(clickEventData);
        this.spreadSheetClick.emit(clickEventData);
    }

    isRowExcluded(row: object): boolean {
        if (this.currentSheet === this.balanceHighlightedRows.sheetName) {
            if (this.balanceHighlightedRows.rows.includes(row[column0Placeholder])) {
                return true;
            }
        }
        if (this.currentSheet === this.incomeHighlightedRows.sheetName) {
            if (this.incomeHighlightedRows.rows.includes(row[column0Placeholder])) {
                return true;
            }
        }
        return false;
    }

    hasMatchingErrors(row: object): boolean {
        if (this.currentSheet === this.balanceHighlightedRows.sheetName) {
            if (this.balanceHighlightedRows.unmatchableAccountRows.includes(row[column0Placeholder])) {
                return true;
            }
        }
        if (this.currentSheet === this.incomeHighlightedRows.sheetName) {
            if (this.incomeHighlightedRows.unmatchableAccountRows.includes(row[column0Placeholder])) {
                return true;
            }
        }
        return false;
    }
}
